import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import fateCardSrc from "../../assets/characters/fate.png";
import closeButtonSrc from "../../assets/stats/button_close.png";
import relicModalCloseButtonSrc from "../../assets/legendarium/button_close.png";
import buttonArrowSrc from "../../assets/button_arrow.png";
import questCornerSrc from "../../assets/stats/stats_ui_01.png";
import startingStatsIconsSrc from "../../assets/stats/stats_ui_02.png";
import playerCardDecorationSrc from "../../assets/stats/stats_ui_03.png";
import upgradedVitalsSrc from "../../assets/stats/stats_ui_04.png";
import exitMainMenuButtonSrc from "../../assets/stats/stats_ui_06.png";
import legendariumButtonSrc from "../../assets/stats/stats_ui_07.png";

const clamp01 = (value) => Math.max(0, Math.min(1, Number(value) || 0));
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const safeWhole = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : fallback;
};
const safePositiveWhole = (value, fallback = 0) =>
  Math.max(0, safeWhole(value, fallback));
const safeInline = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value))
    return value.map(safeInline).filter(Boolean).join(" ");
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

// Helpers to compare geometry and preview state objects. These are used to avoid
// unnecessary state updates when an object's properties have not actually
// changed. In many cases, getBoundingClientRect() returns fresh objects on
// every call, so shallow comparisons would falsely trigger rerenders.
const areRectsEqual = (a, b) =>
  a === b ||
  (!!a &&
    !!b &&
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height);

const areDeckPreviewStatesEqual = (a, b) =>
  a === b ||
  (!!a &&
    !!b &&
    a.key === b.key &&
    a.img === b.img &&
    a.title === b.title &&
    a.description === b.description &&
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width);

const LEFT_BASE_WIDTH = 350;
const RIGHT_BASE_WIDTH = 350;
const COLUMN_BASE_HEIGHT = 860;
const SIDE_PADDING = 18;
const COLUMN_GAP = 18;
const TOP_MARGIN = 18;
const BOTTOM_MARGIN = 18;
const MIN_DECK_WIDTH = 560;
const RIGHT_CONTENT_INSET = 16;
const STATUS_PANEL_TOP_BUTTON_SIZE = "min(4.2vw, 68px)";
const STATUS_PANEL_TOP_BUTTON_MIN_SIZE = 40;
const SCREEN_CORNER_BUTTON_TOP = "clamp(18px, 1.55vw, 22px)";
const SCREEN_CORNER_BUTTON_RIGHT = "clamp(20px, 2vw, 28px)";
const SCREEN_CORNER_BUTTON_GAP = "clamp(8px, 0.8vw, 10px)";
const SCREEN_CORNER_BUTTON_CLOSE_GAP = "clamp(20px, 1.8vw, 26px)";
const STATUS_PANEL_TOP_BUTTON_TRACK_HEIGHT = 68;
const RELIC_MODAL_FONT_FAMILY = "Karma, ui-serif, Georgia, serif";

function capitalize(value) {
  const text = safeInline(value).trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

function getRelicDisplayName(relic, index = 0) {
  const name = safeInline(relic?.name || relic?.title || relic?.id).trim();
  return name || `Relic ${index + 1}`;
}

function getOwnedRelicStatEntries(ownedRelics, statKey) {
  const relics = Array.isArray(ownedRelics) ? ownedRelics.filter(Boolean) : [];

  const entries = relics
    .map((relic, index) => {
      const combat = relic?.combat || {};
      const name = getRelicDisplayName(relic, index);
      const idBase = safeInline(relic?.id || name || statKey || index).trim() || `${statKey}-${index}`;

      if (statKey === "armor") {
        const amount = safePositiveWhole(combat?.startArmor, 0);
        if (!amount) return null;
        return {
          key: `${idBase}-armor`,
          name,
          amount,
          detail: `+${amount} Armor`,
        };
      }

      if (statKey === "reflect") {
        const amount = safePositiveWhole(combat?.startReflectPct, 0);
        if (!amount) return null;
        return {
          key: `${idBase}-reflect`,
          name,
          amount,
          detail: `+${amount}% Reflect`,
        };
      }

      if (statKey === "damage") {
        const flatAmount = safePositiveWhole(combat?.attackBonus, 0);
        const turnStartAmount = safePositiveWhole(combat?.turnStartAttackBonus, 0);
        const amount = flatAmount + turnStartAmount;
        if (!amount) return null;

        const detailParts = [];
        if (flatAmount) detailParts.push(`+${flatAmount} Damage`);
        if (turnStartAmount) detailParts.push(`+${turnStartAmount} Turn-start`);

        return {
          key: `${idBase}-damage`,
          name,
          amount,
          detail: detailParts.join(" · "),
        };
      }

      return null;
    })
    .filter(Boolean)
    .sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name));

  return entries;
}

function buildStartingStatTooltipData({ ownedRelics, armorTotal, reflectTotal, damageTotal }) {
  const armorEntries = getOwnedRelicStatEntries(ownedRelics, "armor");
  const reflectEntries = getOwnedRelicStatEntries(ownedRelics, "reflect");
  const damageEntries = getOwnedRelicStatEntries(ownedRelics, "damage");

  const armorRelicTotal = armorEntries.reduce((acc, entry) => acc + entry.amount, 0);
  const reflectRelicTotal = reflectEntries.reduce((acc, entry) => acc + entry.amount, 0);
  const damageRelicTotal = damageEntries.reduce((acc, entry) => acc + entry.amount, 0);

  return {
    armor: {
      title: "Armor influences",
      emptyLabel: "No trinkets currently affect this Armor value.",
      entries: armorEntries,
      extraAmount: Math.max(0, safePositiveWhole(armorTotal) - armorRelicTotal),
      extraLabel: "Other active bonuses",
      extraSuffix: "Armor",
    },
    reflect: {
      title: "Reflect influences",
      emptyLabel: "No trinkets currently affect this Reflect value.",
      entries: reflectEntries,
      extraAmount: Math.max(0, safePositiveWhole(reflectTotal) - reflectRelicTotal),
      extraLabel: "Other active bonuses",
      extraSuffix: "% Reflect",
    },
    damage: {
      title: "Damage influences",
      emptyLabel: "No trinkets currently affect this Damage value.",
      entries: damageEntries,
      extraAmount: Math.max(0, safePositiveWhole(damageTotal) - damageRelicTotal),
      extraLabel: "Other active bonuses",
      extraSuffix: "Damage",
    },
  };
}

function useViewportSize(active) {
  const getSize = () => ({
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  });

  const [size, setSize] = useState(getSize);

  useEffect(() => {
    if (!active || typeof window === "undefined") return undefined;

    const updateSize = () => setSize(getSize());
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [active]);

  return size;
}

function VitalsWidget({ hp, maxHp, sp, maxSp }) {
  const numberStyle = {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    fontFamily: "Karma, serif",
    fontWeight: 700,
    fontSize: "clamp(14px, 1.2vw, 24px)",
    lineHeight: 1,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    textAlign: "center",
    textShadow:
      "0 2px 8px rgba(0,0,0,0.85), 1px 0 0 rgba(0,0,0,0.95), -1px 0 0 rgba(0,0,0,0.95), 0 1px 0 rgba(0,0,0,0.95), 0 -1px 0 rgba(0,0,0,0.95)",
  };

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "clamp(8px, 1vw, 16px)",
        left: "clamp(7.6px, 0.8vw, 18px)",
        width: "clamp(150px, 12vw, 220px)",
        zIndex: 10030,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <img
        src={upgradedVitalsSrc}
        alt=""
        draggable={false}
        style={{ width: "100%", height: "auto", display: "block" }}
      />

      <div
        style={{ ...numberStyle, left: "19%", top: "60%", color: "#d43333" }}
      >
        {hp}
      </div>
      <div
        style={{ ...numberStyle, left: "19%", top: "79%", color: "#d43333" }}
      >
        {maxHp}
      </div>
      <div
        style={{
          ...numberStyle,
          left: "80.5%",
          top: "16.5%",
          color: "#8b3eff",
        }}
      >
        {sp}
      </div>
      <div
        style={{
          ...numberStyle,
          left: "80.5%",
          top: "33.7%",
          color: "#8b3eff",
        }}
      >
        {maxSp}
      </div>
    </div>
  );
}

function ActionImageButton({
  src,
  alt,
  onClick,
  width = STATUS_PANEL_TOP_BUTTON_SIZE,
  minWidth = STATUS_PANEL_TOP_BUTTON_MIN_SIZE,
  style = {},
  tooltip = alt,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const tooltipVisible = !!tooltip && !!onClick && isHovered;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: "relative",
        zIndex: tooltipVisible ? 2000 : 200,
        width,
        minWidth,
        aspectRatio: "1 / 1",
        border: "none",
        background: "transparent",
        padding: 0,
        cursor: onClick ? "pointer" : "default",
        opacity: onClick ? 1 : 0.6,
        transition: "transform 160ms ease, opacity 160ms ease",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!onClick) return;
        setIsHovered(true);
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        e.currentTarget.style.transform = "translateY(0px)";
      }}
      onFocus={() => {
        if (!onClick) return;
        setIsHovered(true);
      }}
      onBlur={() => setIsHovered(false)}
      aria-label={alt}
      disabled={!onClick}
    >
      {tooltipVisible && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "calc(100% + 10px)",
            transform: "translateX(-50%)",
            padding: "12px 12px 6px",
            borderRadius: 12,
            border: "1px solid rgba(221, 177, 93, 0.98)",
            background: "rgba(0,0,0,0.94)",
            color: "#f3d79d",
            fontFamily: "Karma, serif",
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
            boxShadow: "0 12px 28px rgba(0,0,0,0.46)",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        >
          {tooltip}
        </div>
      )}

      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.38))",
        }}
      />
    </button>
  );
}

function CornerDecorations({ size = 43, inset = 0 }) {
  const baseStyle = {
    position: "absolute",
    width: size,
    height: size,
    pointerEvents: "none",
    userSelect: "none",
    opacity: 0.98,
  };

  return (
    <>
      <img
        src={questCornerSrc}
        alt=""
        draggable={false}
        style={{ ...baseStyle, top: 10, left: inset }}
      />
      <img
        src={questCornerSrc}
        alt=""
        draggable={false}
        style={{ ...baseStyle, top: 10, right: inset, transform: "scaleX(-1)" }}
      />
      <img
        src={questCornerSrc}
        alt=""
        draggable={false}
        style={{
          ...baseStyle,
          bottom: 0,
          left: inset,
          transform: "scaleY(-1)",
        }}
      />
      <img
        src={questCornerSrc}
        alt=""
        draggable={false}
        style={{
          ...baseStyle,
          bottom: 0,
          right: inset,
          transform: "scale(-1, -1)",
        }}
      />
    </>
  );
}

function QuestLogPanel({
  brazierLitCount,
  brazierTotalCount,
  enemySlainCount,
  enemyTotalCount,
}) {
  const quests = [
    {
      key: "brazier",
      label: "Rekindle the Flame",
      current: safePositiveWhole(brazierLitCount),
      total: safePositiveWhole(brazierTotalCount),
    },
    {
      key: "enemy",
      label: "Purge the Halls",
      current: safePositiveWhole(enemySlainCount),
      total: safePositiveWhole(enemyTotalCount),
    },
  ].filter((quest) => quest.total > 0);

  if (!quests.length) {
    return (
      <section
        style={{
          position: "relative",
          width: 300,
          minHeight: 220,
          padding: "28px 18px 30px",
          borderRadius: 18,
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.88), rgba(5,5,5,0.56))",
          boxShadow: "0 16px 40px rgba(0,0,0,0.28)",
        }}
      >
        <CornerDecorations />
        <div
          style={{
            fontFamily: "Karma, serif",
            color: "rgba(255,255,255,0.97)",
            fontWeight: 700,
            fontSize: 27,
            textAlign: "center",
            marginBottom: 18,
          }}
        >
          Quest Log
        </div>
        <div
          style={{
            fontFamily: "Karma, serif",
            color: "rgba(255,255,255,0.62)",
            fontSize: 18,
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          No active floor objectives.
        </div>
      </section>
    );
  }

  return (
    <section
      style={{
        position: "relative",
        width: 300,
        minHeight: 220,
        padding: "28px 18px 34px",
        borderRadius: 18,
        background:
          "linear-gradient(180deg, rgba(10,10,10,0.88), rgba(5,5,5,0.56))",
        boxShadow: "0 16px 40px rgba(0,0,0,0.28)",
      }}
    >
      <CornerDecorations />

      <div
        style={{
          fontFamily: "Karma, serif",
          color: "rgba(255,255,255,0.97)",
          fontWeight: 700,
          fontSize: 27,
          textAlign: "center",
          marginBottom: 16,
        }}
      >
        Quest Log
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {quests.map((quest) => {
          const ratio =
            quest.total > 0 ? clamp01(quest.current / quest.total) : 0;
          return (
            <div key={quest.key}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 10,
                  color: "rgba(192, 192, 192, 0.96)",
                  fontFamily: "Karma, serif",
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: 1.1,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <span style={{ marginRight: 6 }}>·</span>
                  <span>{quest.label}</span>
                </div>
                <div style={{ whiteSpace: "nowrap" }}>
                  {quest.current}/{quest.total}
                </div>
              </div>

              <div
                style={{
                  marginTop: 8,
                  width: "100%",
                  height: 8,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.30)",
                  overflow: "hidden",
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.28)",
                }}
              >
                <div
                  style={{
                    width: `${ratio * 100}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: "linear-gradient(90deg, #c76f2d, #ea9849)",
                    boxShadow: "0 0 10px rgba(228,144,61,0.55)",
                    transition: "width 220ms ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RelicGrid({ ownedRelics, trinketLimit = 20, onRelicClick }) {
  const relics = Array.isArray(ownedRelics) ? ownedRelics.filter(Boolean) : [];
  const visibleSlots = Math.max(20, safePositiveWhole(trinketLimit, 20), relics.length);
  const slots = Array.from(
    { length: visibleSlots },
    (_, index) => relics[index] || null,
  );

  return (
    <section
      style={{
        width: 300,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          width: "100%",
          fontFamily: "Karma, serif",
          color: "rgba(255,255,255,0.96)",
          fontWeight: 700,
          fontSize: 27,
          textAlign: "center",
        }}
      >
        Trinkets ({relics.length})
      </div>

      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
        }}
      >
        {slots.map((relic, index) => {
          const relicName = safeInline(relic?.name || relic?.title || "Relic");
          const interactive = Boolean(
            relic && typeof onRelicClick === "function",
          );
          return (
            <button
              key={`${relic?.id || relicName || "slot"}-${index}`}
              type="button"
              title={relic ? relicName : "Empty slot"}
              onClick={interactive ? () => onRelicClick(index) : undefined}
              disabled={!interactive}
              style={{
                aspectRatio: "1 / 1",
                borderRadius: 14,
                border: "2px solid rgba(255,255,255,0.84)",
                background: relic
                  ? "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))"
                  : "rgba(0,0,0,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                boxShadow: relic ? "0 10px 18px rgba(0,0,0,0.24)" : "none",
                padding: 0,
                cursor: interactive ? "pointer" : "default",
                transition: interactive
                  ? "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease"
                  : "none",
                outline: "none",
              }}
              onMouseEnter={(event) => {
                if (!interactive) return;
                event.currentTarget.style.transform = "translateY(-2px)";
                event.currentTarget.style.boxShadow =
                  "0 14px 24px rgba(0,0,0,0.32)";
                event.currentTarget.style.borderColor =
                  "rgba(255,214,118,0.96)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.transform = "translateY(0px)";
                event.currentTarget.style.boxShadow = relic
                  ? "0 10px 18px rgba(0,0,0,0.24)"
                  : "none";
                event.currentTarget.style.borderColor =
                  "rgba(255,255,255,0.84)";
              }}
            >
              {relic?.image ? (
                <img
                  src={relic.image}
                  alt={relicName}
                  draggable={false}
                  style={{
                    width: "74%",
                    height: "74%",
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              ) : relic ? (
                <div
                  style={{
                    padding: 8,
                    fontFamily: "Karma, serif",
                    color: "rgba(255,255,255,0.92)",
                    fontWeight: 700,
                    fontSize: 12,
                    textAlign: "center",
                    lineHeight: 1.05,
                    pointerEvents: "none",
                  }}
                >
                  {relicName}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function OwnedRelicModal({
  open,
  relics,
  selectedIndex,
  onClose,
  onSelectIndex,
  viewport,
}) {
  const relicList = Array.isArray(relics) ? relics.filter(Boolean) : [];
  const hasValidIndex =
    Number.isInteger(selectedIndex) &&
    selectedIndex >= 0 &&
    selectedIndex < relicList.length;

  useEffect(() => {
    if (!open || !hasValidIndex) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (relicList.length <= 1) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onSelectIndex?.(
          (selectedIndex - 1 + relicList.length) % relicList.length,
        );
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        onSelectIndex?.((selectedIndex + 1) % relicList.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    hasValidIndex,
    onClose,
    onSelectIndex,
    relicList.length,
    selectedIndex,
  ]);

  if (!open || !hasValidIndex) return null;

  const relic = relicList[selectedIndex];
  const compactRelicModal = viewport.width <= 1600 || viewport.height <= 960;
  const tightRelicModal = viewport.width <= 1260 || viewport.height <= 840;
  const stackedRelicModal = viewport.width <= 980;
  const canCycleRelics = relicList.length > 1;
  const titleText = [
    safeInline(relic?.numeral).trim(),
    safeInline(relic?.name || relic?.title || "Relic").trim(),
  ]
    .filter(Boolean)
    .join(" - ");

  return (
    <div
      onClick={() => onClose?.()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12050,
        background: "rgba(0,0,0,0.88)",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          minHeight: "100vh",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: stackedRelicModal
            ? "84px 28px 34px"
            : tightRelicModal
              ? "20px 74px 28px"
              : compactRelicModal
                ? "24px 92px 32px"
                : "28px 112px 36px",
        }}
      >
        <button
          type="button"
          onClick={() => onClose?.()}
          style={{
            position: "fixed",
            top: SCREEN_CORNER_BUTTON_TOP,
            right: SCREEN_CORNER_BUTTON_RIGHT,
            width: STATUS_PANEL_TOP_BUTTON_SIZE,
            minWidth: STATUS_PANEL_TOP_BUTTON_MIN_SIZE,
            aspectRatio: "1 / 1",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: 0,
            zIndex: 5,
          }}
        >
          <img
            src={relicModalCloseButtonSrc}
            alt="Close relic modal"
            draggable={false}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </button>

        <button
          type="button"
          onClick={() =>
            canCycleRelics &&
            onSelectIndex?.(
              (selectedIndex - 1 + relicList.length) % relicList.length,
            )
          }
          disabled={!canCycleRelics}
          aria-label="Previous owned relic"
          style={{
            position: "absolute",
            left: stackedRelicModal
              ? 42
              : tightRelicModal
                ? "calc(50% - 384px)"
                : compactRelicModal
                  ? "calc(50% - 445px)"
                  : "calc(50% - 512px)",
            top: "50%",
            transform: stackedRelicModal
              ? "translateY(-50%)"
              : "translate(-100%, -50%)",
            width: stackedRelicModal
              ? 34
              : tightRelicModal
                ? 42
                : compactRelicModal
                  ? 50
                  : 58,
            height: stackedRelicModal
              ? 58
              : tightRelicModal
                ? 72
                : compactRelicModal
                  ? 84
                  : 96,
            border: "none",
            background: "transparent",
            padding: stackedRelicModal
              ? "10px 18px 10px 10px"
              : tightRelicModal
                ? "12px 34px 12px 16px"
                : compactRelicModal
                  ? "14px 40px 14px 18px"
                  : "16px 48px 16px 22px",
            boxSizing: "content-box",
            cursor: canCycleRelics ? "pointer" : "default",
            opacity: canCycleRelics ? 0.96 : 0.28,
            zIndex: 6,
          }}
        >
          <img
            src={buttonArrowSrc}
            alt=""
            aria-hidden
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "contain",
            }}
          />
        </button>

        <button
          type="button"
          onClick={() =>
            canCycleRelics &&
            onSelectIndex?.((selectedIndex + 1) % relicList.length)
          }
          disabled={!canCycleRelics}
          aria-label="Next owned relic"
          style={{
            position: "absolute",
            right: stackedRelicModal
              ? 42
              : tightRelicModal
                ? "calc(50% - 384px)"
                : compactRelicModal
                  ? "calc(50% - 445px)"
                  : "calc(50% - 512px)",
            top: "50%",
            transform: stackedRelicModal
              ? "translateY(-50%)"
              : "translate(100%, -50%)",
            width: stackedRelicModal
              ? 34
              : tightRelicModal
                ? 42
                : compactRelicModal
                  ? 50
                  : 58,
            height: stackedRelicModal
              ? 58
              : tightRelicModal
                ? 72
                : compactRelicModal
                  ? 84
                  : 96,
            border: "none",
            background: "transparent",
            padding: stackedRelicModal
              ? "10px 10px 10px 18px"
              : tightRelicModal
                ? "12px 16px 12px 34px"
                : compactRelicModal
                  ? "14px 18px 14px 40px"
                  : "16px 22px 16px 48px",
            boxSizing: "content-box",
            cursor: canCycleRelics ? "pointer" : "default",
            opacity: canCycleRelics ? 0.96 : 0.28,
            zIndex: 6,
          }}
        >
          <img
            src={buttonArrowSrc}
            alt=""
            aria-hidden
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "contain",
              transform: "scaleX(-1)",
            }}
          />
        </button>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: stackedRelicModal
              ? "minmax(0, 1fr)"
              : tightRelicModal
                ? "minmax(240px, 300px) minmax(360px, 430px)"
                : compactRelicModal
                  ? "minmax(270px, 340px) minmax(430px, 520px)"
                  : "minmax(300px, 390px) minmax(500px, 610px)",
            gap: stackedRelicModal ? 14 : tightRelicModal ? 16 : 24,
            alignItems: "center",
            justifyContent: "center",
            width: "min(100%, 1020px)",
          }}
        >
          <div
            style={{
              position: "relative",
              minHeight: stackedRelicModal ? 220 : tightRelicModal ? 270 : 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                width: stackedRelicModal
                  ? 170
                  : tightRelicModal
                    ? 205
                    : compactRelicModal
                      ? 245
                      : 275,
                height: stackedRelicModal
                  ? 170
                  : tightRelicModal
                    ? 205
                    : compactRelicModal
                      ? 245
                      : 275,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,196,82,0.18) 0%, rgba(255,160,40,0.08) 34%, rgba(0,0,0,0) 74%)",
                filter: "blur(18px)",
                transform: "translateY(8%)",
                pointerEvents: "none",
              }}
            />
            {relic?.image ? (
              <img
                src={relic.image}
                alt={safeInline(relic?.name || relic?.title || "Relic")}
                draggable={false}
                style={{
                  position: "relative",
                  width: stackedRelicModal ? "min(44vw, 205px)" : "100%",
                  maxWidth: stackedRelicModal
                    ? 205
                    : tightRelicModal
                      ? 210
                      : compactRelicModal
                        ? 250
                        : 285,
                  maxHeight: stackedRelicModal ? 205 : "min(42vh, 335px)",
                  objectFit: "contain",
                  filter: "drop-shadow(0 18px 36px rgba(0,0,0,0.62))",
                  userSelect: "none",
                }}
              />
            ) : (
              <div
                style={{
                  position: "relative",
                  width: stackedRelicModal ? "min(44vw, 205px)" : "100%",
                  maxWidth: stackedRelicModal
                    ? 205
                    : tightRelicModal
                      ? 210
                      : compactRelicModal
                        ? 250
                        : 285,
                  minHeight: stackedRelicModal
                    ? 160
                    : tightRelicModal
                      ? 210
                      : 260,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#f4f0e8",
                  fontFamily: RELIC_MODAL_FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: stackedRelicModal ? 18 : 24,
                  textAlign: "center",
                  textShadow: "0 2px 8px rgba(0,0,0,0.92)",
                }}
              >
                {safeInline(relic?.name || relic?.title || "Relic")}
              </div>
            )}
          </div>

          <div
            style={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: stackedRelicModal ? "center" : "flex-start",
              textAlign: stackedRelicModal ? "center" : "left",
            }}
          >
            <div
              style={{
                color: "#f4f0e8",
                fontFamily: RELIC_MODAL_FONT_FAMILY,
                fontWeight: 700,
                fontSize: stackedRelicModal
                  ? "clamp(22px, 4.6vw, 32px)"
                  : "clamp(22px, 1.7vw, 32px)",
                lineHeight: 1.02,
                marginBottom: 12,
                textShadow: "0 2px 8px rgba(0,0,0,0.92)",
              }}
            >
              {titleText || "Relic"}
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: tightRelicModal ? "7px 14px 4px" : "8px 16px 5px",
                borderRadius: 999,
                border: "1px solid rgba(255,208,104,0.82)",
                color: "#ffd866",
                fontFamily: RELIC_MODAL_FONT_FAMILY,
                fontWeight: 700,
                fontSize: tightRelicModal ? 16 : 18,
                lineHeight: 1,
                background: "rgba(35,20,0,0.38)",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.35) inset",
                marginBottom: tightRelicModal ? 14 : 16,
              }}
            >
              {capitalize(relic?.rarity) || "Relic"}
            </div>

            <div
              style={{
                color: "rgba(244,240,232,0.92)",
                fontFamily: RELIC_MODAL_FONT_FAMILY,
                fontWeight: 400,
                fontSize: stackedRelicModal
                  ? "clamp(16px, 2.6vw, 20px)"
                  : "clamp(16px, 1.08vw, 20px)",
                lineHeight: 1.18,
                whiteSpace: "pre-wrap",
                maxWidth: stackedRelicModal
                  ? "min(100%, 640px)"
                  : tightRelicModal
                    ? 420
                    : compactRelicModal
                      ? 500
                      : 560,
                textShadow: "0 2px 8px rgba(0,0,0,0.9)",
              }}
            >
              {safeInline(relic?.description).trim() ||
                "No description available."}
            </div>

            <div
              style={{
                marginTop: tightRelicModal ? 18 : 22,
                color: "#f4f0e8",
                fontFamily: RELIC_MODAL_FONT_FAMILY,
                fontWeight: 700,
                fontSize: stackedRelicModal ? 20 : tightRelicModal ? 22 : 26,
                lineHeight: 1,
                textShadow: "0 2px 8px rgba(0,0,0,0.95)",
              }}
            >
              {selectedIndex + 1}/{relicList.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StartingStatTooltip({ open, anchorRect, data }) {
  if (!open || !anchorRect || !data || typeof document === "undefined") {
    return null;
  }

  const tooltipWidth = 320;
  const viewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 900 : window.innerHeight;
  const left = clamp(anchorRect.right + 14, 16, Math.max(16, viewportWidth - tooltipWidth - 16));
  const top = clamp(anchorRect.top + anchorRect.height / 2, 18, Math.max(18, viewportHeight - 18));

  return createPortal(
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        left,
        top,
        transform: "translateY(-50%)",
        width: tooltipWidth,
        maxWidth: "min(320px, calc(100vw - 32px))",
        maxHeight: "min(52vh, 360px)",
        overflowY: "auto",
        padding: "12px 14px 11px",
        borderRadius: 14,
        border: "1px solid rgba(221, 177, 93, 0.98)",
        background: "rgba(0,0,0,0.94)",
        color: "#f3d79d",
        fontFamily: "Karma, serif",
        boxShadow: "0 12px 28px rgba(0,0,0,0.46)",
        pointerEvents: "none",
        zIndex: 10120,
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: "nowrap",
          marginBottom: data.entries?.length || data.extraAmount ? 10 : 0,
        }}
      >
        {data.title}
      </div>

      {data.entries?.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.entries.map((entry) => (
            <div
              key={entry.key}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                gap: 12,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  color: "rgba(255,244,217,0.98)",
                  fontSize: 17,
                  fontWeight: 700,
                  lineHeight: 1.06,
                  minWidth: 0,
                }}
              >
                {entry.name}
              </div>
              <div
                style={{
                  color: "#f3d79d",
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.06,
                  whiteSpace: "nowrap",
                }}
              >
                {entry.detail}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            color: "rgba(255,232,189,0.86)",
            fontSize: 16,
            lineHeight: 1.16,
          }}
        >
          {data.emptyLabel}
        </div>
      )}

      {data.extraAmount > 0 && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid rgba(221, 177, 93, 0.32)",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div
            style={{
              color: "rgba(255,232,189,0.84)",
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.06,
            }}
          >
            {data.extraLabel}
          </div>
          <div
            style={{
              color: "#f3d79d",
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.06,
              whiteSpace: "nowrap",
            }}
          >
            +{data.extraAmount} {data.extraSuffix}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

function StartingStatIcons({ statValues, tooltipData }) {
  const [hoveredStatKey, setHoveredStatKey] = useState(null);
  const [tooltipAnchorRect, setTooltipAnchorRect] = useState(null);
  const statZoneRefs = useRef(new Map());
  const statKeys = ["armor", "reflect", "damage"];
  const numberTops = ["16%", "47.5%", "79.5%"];

  const openTooltip = (statKey) => {
    setHoveredStatKey(statKey);
    const node = statZoneRefs.current.get(statKey);
    setTooltipAnchorRect(node?.getBoundingClientRect() || null);
  };

  const closeTooltip = () => {
    setHoveredStatKey(null);
    setTooltipAnchorRect(null);
  };

  useEffect(() => {
    if (!hoveredStatKey || typeof window === "undefined") return undefined;

    let rafId = null;

    const syncTooltipRect = () => {
      const node = statZoneRefs.current.get(hoveredStatKey);
      const nextRect = node?.getBoundingClientRect() || null;
      setTooltipAnchorRect((current) =>
        areRectsEqual(current, nextRect) ? current : nextRect,
      );
    };

    const scheduleTooltipRectSync = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        syncTooltipRect();
      });
    };

    // Immediately schedule a sync so the tooltip anchors correctly on mount.
    scheduleTooltipRectSync();
    window.addEventListener("resize", scheduleTooltipRectSync);
    window.addEventListener("scroll", scheduleTooltipRectSync, true);

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", scheduleTooltipRectSync);
      window.removeEventListener("scroll", scheduleTooltipRectSync, true);
    };
  }, [hoveredStatKey]);

  return (
    <div
      style={{
        position: "relative",
        width: 84,
        minHeight: 184,
        display: "flex",
        alignItems: "stretch",
      }}
    >
      <img
        src={startingStatsIconsSrc}
        alt=""
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          userSelect: "none",
          pointerEvents: "none",
          filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.36))",
          transform: "translate(0px, -14px)",
        }}
      />

      {statKeys.map((statKey, index) => (
        <button
          key={statKey}
          type="button"
          ref={(node) => {
            if (node) statZoneRefs.current.set(statKey, node);
            else statZoneRefs.current.delete(statKey);
          }}
          onMouseEnter={() => openTooltip(statKey)}
          onMouseLeave={closeTooltip}
          onFocus={() => openTooltip(statKey)}
          onBlur={closeTooltip}
          aria-label={`${capitalize(statKey)} trinket influences`}
          style={{
            position: "absolute",
            left: "50%",
            top: numberTops[index],
            width: 58,
            height: 54,
            transform: "translate(-50%, -50%)",
            border: "none",
            background: "transparent",
            padding: 0,
            margin: 0,
            cursor: "help",
            zIndex: 2,
          }}
        />
      ))}

      {numberTops.map((top, index) => (
        <div
          key={top}
          style={{
            position: "absolute",
            left: "50%",
            top,
            transform: "translate(-50%, -50%)",
            fontFamily: "Karma, serif",
            color: "rgba(255,255,255,0.98)",
            fontWeight: 700,
            fontSize: 32,
            textShadow:
              "0 2px 8px rgba(0,0,0,0.85), 1px 0 0 rgba(0,0,0,0.95), -1px 0 0 rgba(0,0,0,0.95), 0 1px 0 rgba(0,0,0,0.95), 0 -1px 0 rgba(0,0,0,0.95)",
            pointerEvents: "none",
            zIndex: 3,
          }}
        >
          +{statValues[index]}
        </div>
      ))}

      <StartingStatTooltip
        open={!!hoveredStatKey}
        anchorRect={tooltipAnchorRect}
        data={hoveredStatKey ? tooltipData?.[hoveredStatKey] : null}
      />
    </div>
  );
}

function PlayerColumn({
  playerLevel,
  playerXp,
  xpToNextLevel,
  startingArmorValue,
  startingReflectValue,
  startingDamageValue,
  startingStatTooltipData,
}) {
  const xpRatio = xpToNextLevel > 0 ? clamp01(playerXp / xpToNextLevel) : 0;
  const statValues = [
    startingArmorValue,
    startingReflectValue,
    startingDamageValue,
  ];
  const statLabelStyle = {
    fontFamily: "Karma, serif",
    color: "rgba(255,255,255,0.94)",
    fontWeight: 700,
    fontSize: 20,
    lineHeight: 1.7,
  };

  return (
    <div
      style={{
        width: LEFT_BASE_WIDTH,
        height: COLUMN_BASE_HEIGHT,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        boxSizing: "border-box",
      }}
    >
      <div style={{ width: 316, display: "flex", justifyContent: "center" }}>
        <img
          src={fateCardSrc}
          alt="Fate"
          draggable={false}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            filter: "drop-shadow(0 22px 40px rgba(0,0,0,0.42))",
            transform: "translate(0px, 30px)",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: 10,
          width: 316,
        }}
      >
        <img
          src={playerCardDecorationSrc}
          alt=""
          draggable={false}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            opacity: 0.98,
            userSelect: "none",
            pointerEvents: "none",
            transform: "translate(0px, 30px)",
          }}
        />
      </div>

      <div style={{ marginTop: 26, width: 320 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            fontFamily: "Karma, serif",
            color: "rgba(255,255,255,0.97)",
            fontWeight: 700,
          }}
        >
          <div style={{ fontSize: 27 }}>Level {playerLevel}</div>
          <div style={{ fontSize: 20 }}>
            {playerXp}/{xpToNextLevel}
          </div>
        </div>

        <div
          style={{
            marginTop: 8,
            width: "100%",
            height: 8,
            borderRadius: 999,
            background: "rgba(255,255,255,0.30)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${xpRatio * 100}%`,
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #c76f2d, #ea9849)",
              transition: "width 220ms ease",
            }}
          />
        </div>
      </div>

      <div
        style={{
          marginTop: 30,
          width: 320,
          display: "grid",
          gridTemplateColumns: "1fr 84px",
          columnGap: 16,
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateRows: "repeat(3, minmax(0, 1fr))",
            rowGap: 20,
            paddingTop: 10,
            paddingBottom: 20,
          }}
        >
          <div style={statLabelStyle}>Starting Armor:</div>
          <div style={statLabelStyle}>Starting Reflect:</div>
          <div style={statLabelStyle}>Starting Damage:</div>
        </div>

        <StartingStatIcons
          statValues={statValues}
          tooltipData={startingStatTooltipData}
        />
      </div>
    </div>
  );
}

// DeckGrid displays the player's current deck. It now accepts an `open` prop so
// it can disable expensive pointer move handling when the overlay is closed.
function DeckGrid({ deckCards, minCardWidth = 182, open = false }) {
  const cards = Array.isArray(deckCards) ? deckCards : [];
  const [hoveredDeckCard, setHoveredDeckCard] = useState(null);
  const [hoveredDeckPreview, setHoveredDeckPreview] = useState(null);
  // Track whether we are in a hover recovery phase after scrolling. When true,
  // a temporary pointermove listener will be attached to detect the next valid
  // hover target.
  const [isDeckHoverRecoveryPending, setIsDeckHoverRecoveryPending] =
    useState(false);
  const deckCardNodesRef = useRef(new Map());
  const suppressDeckHoverRef = useRef(false);
  const pointerPositionRef = useRef({ x: 0, y: 0 });
  const lockedPointerPositionRef = useRef(null);
  const deckCardDataMap = useMemo(() => {
    const map = new Map();

    cards.forEach((card, index) => {
      const key =
        card?.id ||
        `${safeInline(card?.name || card?.title || "card")}-${index}`;
      map.set(key, {
        img: card?.img || null,
        title: safeInline(
          card?.name || card?.title || card?.id || "Unknown card",
        ),
        description: safeInline(card?.text || card?.desc || ""),
      });
    });

    return map;
  }, [cards]);

  const syncHoveredDeckPreview = (key, cardData = null) => {
    const node = deckCardNodesRef.current.get(key);
    if (!node) return;

    const rect = node.getBoundingClientRect();

    setHoveredDeckPreview((current) => {
      const base = cardData || current;
      if (!base || (current && current.key !== key && !cardData)) {
        return current;
      }

      const nextPreview = {
        key,
        img: base.img || null,
        title: base.title || "Unknown card",
        description: base.description || "",
        left: rect.left,
        top: rect.top,
        width: rect.width,
      };

      return areDeckPreviewStatesEqual(current, nextPreview)
        ? current
        : nextPreview;
    });
  };

  const handleDeckCardEnter = (key, cardData) => {
    if (suppressDeckHoverRef.current) return;

    setHoveredDeckCard(key);
    syncHoveredDeckPreview(key, cardData);
  };

  const clearHoveredDeckCard = (key) => {
    if (suppressDeckHoverRef.current) return;

    setHoveredDeckCard((current) => (current === key ? null : current));
    setHoveredDeckPreview((current) => (current?.key === key ? null : current));
  };

  const handleDeckScroll = () => {
    // Enter the hover recovery phase only once when scrolling begins.
    if (!suppressDeckHoverRef.current) {
      suppressDeckHoverRef.current = true;
      setIsDeckHoverRecoveryPending(true);
    }

    // Capture the pointer location at the time of scroll; we will require
    // movement beyond a small threshold before re-enabling hover.
    lockedPointerPositionRef.current = { ...pointerPositionRef.current };

    // Clear hovered state only when a card was previously hovered.
    setHoveredDeckCard((current) => (current == null ? current : null));
    setHoveredDeckPreview((current) => (current == null ? current : null));
  };

  // Track pointer position inside the deck scroll container. This avoids the
  // need for a global pointermove listener during normal use; we only need
  // pointermove globally during the short hover recovery window.
  const handleDeckPointerMove = (event) => {
    pointerPositionRef.current = { x: event.clientX, y: event.clientY };
  };

  // When a card is hovered, schedule preview alignment updates on resize using
  // requestAnimationFrame. This prevents frequent layout reads during rapid
  // resize events.
  useEffect(() => {
    if (!hoveredDeckCard || typeof window === "undefined") return undefined;

    let rafId = null;
    const scheduleHoveredDeckPreviewRefresh = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        syncHoveredDeckPreview(hoveredDeckCard);
      });
    };

    // Perform an initial sync in case the component mounted mid-resize.
    scheduleHoveredDeckPreviewRefresh();
    window.addEventListener("resize", scheduleHoveredDeckPreviewRefresh);
    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", scheduleHoveredDeckPreviewRefresh);
    };
  }, [hoveredDeckCard]);

  // During a short hover recovery phase after scrolling, attach a global
  // pointermove listener that is throttled via requestAnimationFrame. When
  // the pointer has moved far enough from its locked position, hover is
  // restored and the listener is removed. Otherwise, the listener remains
  // inactive during normal deck browsing.
  useEffect(() => {
    if (!isDeckHoverRecoveryPending || typeof window === "undefined") {
      return undefined;
    }

    let rafId = null;
    let latestPointerEvent = null;

    const flushPointerMove = () => {
      rafId = null;
      const event = latestPointerEvent;
      latestPointerEvent = null;
      if (!event) return;

      const nextPosition = { x: event.clientX, y: event.clientY };
      pointerPositionRef.current = nextPosition;

      if (!suppressDeckHoverRef.current) {
        setIsDeckHoverRecoveryPending(false);
        return;
      }

      const lockedPosition = lockedPointerPositionRef.current;
      if (!lockedPosition) return;

      const movedEnough =
        Math.abs(nextPosition.x - lockedPosition.x) > 2 ||
        Math.abs(nextPosition.y - lockedPosition.y) > 2;

      if (!movedEnough) return;

      suppressDeckHoverRef.current = false;
      lockedPointerPositionRef.current = null;
      setIsDeckHoverRecoveryPending(false);

      const hoveredElement = document.elementFromPoint(
        nextPosition.x,
        nextPosition.y,
      );
      const hoveredCardNode = hoveredElement?.closest?.(
        "[data-deck-card-key]",
      );
      const nextKey = hoveredCardNode?.getAttribute("data-deck-card-key");

      if (nextKey) {
        const nextCardData = deckCardDataMap.get(nextKey);
        if (nextCardData) {
          setHoveredDeckCard(nextKey);
          syncHoveredDeckPreview(nextKey, nextCardData);
          return;
        }
      }

      setHoveredDeckCard(null);
      setHoveredDeckPreview(null);
    };

    const handlePointerMove = (event) => {
      latestPointerEvent = event;
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(flushPointerMove);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      latestPointerEvent = null;
      window.removeEventListener("pointermove", handlePointerMove);
    };
  }, [deckCardDataMap, isDeckHoverRecoveryPending]);

  const renderDeckCardFace = (cardData, hovered = false) => (
    <div
      style={{
        borderRadius: 12,
        overflow: "hidden",
        background: "rgba(8,8,8,0.72)",
        boxShadow: hovered
          ? "0 26px 46px rgba(0,0,0,0.44)"
          : "0 18px 32px rgba(0,0,0,0.28)",
        border: "1px solid rgba(255,255,255,0.09)",
        transform: hovered ? "scale(1.50)" : "scale(1)",
        transformOrigin: "center center",
        transition:
          "transform 220ms ease, box-shadow 220ms ease, opacity 140ms ease",
      }}
    >
      {cardData?.img ? (
        <img
          src={cardData.img}
          alt={cardData.title}
          draggable={false}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      ) : (
        <div
          style={{
            aspectRatio: "0.72 / 1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "18px 16px",
            background:
              "linear-gradient(180deg, rgba(48,48,48,0.68), rgba(18,18,18,0.92))",
          }}
        >
          <div
            style={{
              color: "rgba(255,255,255,0.95)",
              fontFamily: "Karma, serif",
              fontWeight: 700,
              fontSize: 20,
              textAlign: "center",
              lineHeight: 1.05,
            }}
          >
            {cardData.title}
          </div>
          {!!cardData.description && (
            <div
              style={{
                color: "rgba(255,255,255,0.72)",
                fontFamily: "Karma, serif",
                fontSize: 14,
                textAlign: "center",
                lineHeight: 1.18,
                marginTop: 12,
              }}
            >
              {cardData.description}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        className="deck-scroll-hidden"
        onScroll={handleDeckScroll}
        onPointerMove={handleDeckPointerMove}
        style={{
          minHeight: 0,
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: 64, // raise/lower this number
          paddingRight: 2,
          paddingBottom: 110,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div style={{ paddingBottom: 10, marginBottom: 8 }}>
          <div
            style={{
              fontFamily: "Karma, serif",
              color: "rgba(255,255,255,0.96)",
              fontWeight: 700,
              fontSize: "clamp(22px, 1.5vw, 30px)",
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            Deck ({cards.length})
          </div>
        </div>

        {!deckCards ? (
          <div
            style={{
              fontFamily: "Karma, serif",
              color: "rgba(255,255,255,0.65)",
              fontSize: 18,
              textAlign: "center",
              paddingTop: 30,
            }}
          >
            Loading deck…
          </div>
        ) : cards.length === 0 ? (
          <div
            style={{
              fontFamily: "Karma, serif",
              color: "rgba(255,255,255,0.65)",
              fontSize: 18,
              textAlign: "center",
              paddingTop: 30,
            }}
          >
            No cards found in deck.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              width: "100%",
              gridTemplateColumns: `repeat(auto-fit, minmax(${minCardWidth}px, 1fr))`,
              gap: "clamp(12px, 0.9vw, 18px)",
              alignItems: "start",
              justifyContent: "center",
              margin: "0 auto",
              overflow: "visible",
            }}
          >
            {cards.map((card, index) => {
              const key =
                card?.id ||
                `${safeInline(card?.name || card?.title || "card")}-${index}`;
              const title = safeInline(
                card?.name || card?.title || card?.id || "Unknown card",
              );
              const description = safeInline(card?.text || card?.desc || "");
              const hovered = hoveredDeckCard === key;
              const cardData = {
                img: card?.img || null,
                title,
                description,
              };

              return (
                <div
                  key={key}
                  data-deck-card-key={key}
                  ref={(node) => {
                    if (node) deckCardNodesRef.current.set(key, node);
                    else deckCardNodesRef.current.delete(key);
                  }}
                  title={title}
                  onMouseEnter={() => handleDeckCardEnter(key, cardData)}
                  onMouseLeave={() => clearHoveredDeckCard(key)}
                  onFocus={() => handleDeckCardEnter(key, cardData)}
                  onBlur={() => clearHoveredDeckCard(key)}
                  style={{
                    position: "relative",
                    overflow: "visible",
                    zIndex: hovered ? 4 : 1,
                  }}
                >
                  <div
                    style={{
                      opacity:
                        hovered && hoveredDeckPreview?.key === key ? 0 : 1,
                    }}
                  >
                    {renderDeckCardFace(cardData, false)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {hoveredDeckPreview && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: hoveredDeckPreview.left + hoveredDeckPreview.width / 2,
            top: hoveredDeckPreview.top,
            width: hoveredDeckPreview.width,
            transform: "translate(-50%, 0)",
            zIndex: 10020,
            pointerEvents: "none",
          }}
        >
          {renderDeckCardFace(hoveredDeckPreview, true)}
        </div>
      )}
    </>
  );
}

function RightColumn({
  ownedRelics,
  trinketLimit = 20,
  brazierLitCount,
  brazierTotalCount,
  enemySlainCount,
  enemyTotalCount,
  onOpenRelicModal,
}) {
  return (
    <div
      style={{
        width: RIGHT_BASE_WIDTH,
        height: COLUMN_BASE_HEIGHT,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        boxSizing: "border-box",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: "100%",
          minHeight: STATUS_PANEL_TOP_BUTTON_TRACK_HEIGHT,
        }}
      />

      <div
        style={{
          width: 300,
          marginTop: 22,
          marginRight: RIGHT_CONTENT_INSET,
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        <QuestLogPanel
          brazierLitCount={brazierLitCount}
          brazierTotalCount={brazierTotalCount}
          enemySlainCount={enemySlainCount}
          enemyTotalCount={enemyTotalCount}
        />

        <RelicGrid
          ownedRelics={ownedRelics}
          trinketLimit={trinketLimit}
          onRelicClick={onOpenRelicModal}
        />
      </div>
    </div>
  );
}

export default function StatusPanelOverlay({
  open,
  onClose,
  onOpenLegendarium,
  onExitToMainMenu,
  deckCards,
  ownedRelics,
  trinketLimit = 20,
  playerLevel,
  playerXp,
  xpToNextLevel,
  hp,
  maxHp,
  sp,
  maxSp,
  brazierLitCount,
  brazierTotalCount,
  enemySlainCount,
  enemyTotalCount,
  startingArmorValue = 0,
  startingReflectValue = 0,
  startingDamageValue = 0,
}) {
  const viewport = useViewportSize(open);
  const [selectedRelicIndex, setSelectedRelicIndex] = useState(null);

  useEffect(() => {
    if (!open) setSelectedRelicIndex(null);
  }, [open]);

  useEffect(() => {
    const relicCount = Array.isArray(ownedRelics)
      ? ownedRelics.filter(Boolean).length
      : 0;
    if (!relicCount) {
      setSelectedRelicIndex(null);
      return;
    }
    setSelectedRelicIndex((prev) =>
      prev == null ? prev : Math.min(prev, relicCount - 1),
    );
  }, [ownedRelics]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (selectedRelicIndex != null) {
        setSelectedRelicIndex(null);
        return;
      }
      onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, selectedRelicIndex]);

  const safeLevel = Math.max(1, safeWhole(playerLevel, 1));
  const safeXp = safePositiveWhole(playerXp, 0);
  const safeNeed = Math.max(1, safeWhole(xpToNextLevel, 1));
  const safeHp = safeWhole(hp, 0);
  const safeMaxHp = Math.max(1, safeWhole(maxHp, 1));
  const safeSp = safeWhole(sp, 0);
  const safeMaxSp = Math.max(1, safeWhole(maxSp, 1));

  const computedStartingValues = useMemo(
    () => ({
      armor: safePositiveWhole(startingArmorValue),
      reflect: safePositiveWhole(startingReflectValue),
      damage: safePositiveWhole(startingDamageValue),
    }),
    [startingArmorValue, startingDamageValue, startingReflectValue],
  );

  const startingStatTooltipData = useMemo(
    () =>
      buildStartingStatTooltipData({
        ownedRelics,
        armorTotal: computedStartingValues.armor,
        reflectTotal: computedStartingValues.reflect,
        damageTotal: computedStartingValues.damage,
      }),
    [
      ownedRelics,
      computedStartingValues.armor,
      computedStartingValues.reflect,
      computedStartingValues.damage,
    ],
  );

  const layout = useMemo(() => {
    const availableHeight = Math.max(
      520,
      viewport.height - TOP_MARGIN - BOTTOM_MARGIN,
    );
    const heightScale = availableHeight / COLUMN_BASE_HEIGHT;
    const widthScale =
      (viewport.width - SIDE_PADDING * 2 - COLUMN_GAP * 2 - MIN_DECK_WIDTH) /
      (LEFT_BASE_WIDTH + RIGHT_BASE_WIDTH);

    const columnScale = clamp(Math.min(heightScale, widthScale), 0.7, 1.45);
    const leftWidth = LEFT_BASE_WIDTH * columnScale;
    const rightWidth = RIGHT_BASE_WIDTH * columnScale;
    const deckLeft = SIDE_PADDING + leftWidth + COLUMN_GAP;
    const deckRight = SIDE_PADDING + rightWidth + COLUMN_GAP;
    const deckWidth = Math.max(220, viewport.width - deckLeft - deckRight);

    let minCardWidth = 190;
    if (deckWidth < 980) minCardWidth = 172;
    if (deckWidth < 760) minCardWidth = 156;

    return {
      availableHeight,
      columnScale,
      leftWidth,
      rightWidth,
      deckLeft,
      deckRight,
      minCardWidth,
    };
  }, [viewport.height, viewport.width]);

  if (!open) return null;

  return (
    <div
      data-ui="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(2px)",
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <VitalsWidget
        hp={safeHp}
        maxHp={safeMaxHp}
        sp={safeSp}
        maxSp={safeMaxSp}
      />

      <div
        style={{
          position: "fixed",
          top: SCREEN_CORNER_BUTTON_TOP,
          right: SCREEN_CORNER_BUTTON_RIGHT,
          display: "flex",
          alignItems: "center",
          gap: SCREEN_CORNER_BUTTON_GAP,
          zIndex: 10080,
        }}
      >
        <ActionImageButton
          src={exitMainMenuButtonSrc}
          alt="Exit to Main Menu"
          tooltip="Exit to Main Menu"
          onClick={onExitToMainMenu}
          width={STATUS_PANEL_TOP_BUTTON_SIZE}
          minWidth={STATUS_PANEL_TOP_BUTTON_MIN_SIZE}
        />
        <ActionImageButton
          src={legendariumButtonSrc}
          alt="Open the Legendarium"
          tooltip="Open the Legendarium"
          onClick={onOpenLegendarium}
          width={STATUS_PANEL_TOP_BUTTON_SIZE}
          minWidth={STATUS_PANEL_TOP_BUTTON_MIN_SIZE}
        />
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "relative",
            zIndex: 200,
            width: STATUS_PANEL_TOP_BUTTON_SIZE,
            minWidth: STATUS_PANEL_TOP_BUTTON_MIN_SIZE,
            aspectRatio: "1 / 1",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: 0,
            marginLeft: SCREEN_CORNER_BUTTON_CLOSE_GAP,
          }}
          aria-label="Close status screen"
        >
          <img
            src={closeButtonSrc}
            alt="Close"
            draggable={false}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </button>
      </div>

      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          fontFamily: "Karma, serif",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: TOP_MARGIN,
            left: SIDE_PADDING,
            width: layout.leftWidth,
            height: layout.availableHeight,
            overflow: "visible",
          }}
        >
          <div
            style={{
              width: LEFT_BASE_WIDTH,
              height: COLUMN_BASE_HEIGHT,
              transform: `scale(${layout.columnScale})`,
              transformOrigin: "top left",
            }}
          >
            <PlayerColumn
              playerLevel={safeLevel}
              playerXp={safeXp}
              xpToNextLevel={safeNeed}
              startingArmorValue={computedStartingValues.armor}
              startingReflectValue={computedStartingValues.reflect}
              startingDamageValue={computedStartingValues.damage}
              startingStatTooltipData={startingStatTooltipData}
            />
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            top: TOP_MARGIN,
            right: SIDE_PADDING,
            width: layout.rightWidth,
            height: layout.availableHeight,
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: RIGHT_BASE_WIDTH,
              height: COLUMN_BASE_HEIGHT,
              transform: `scale(${layout.columnScale})`,
              transformOrigin: "top right",
            }}
          >
            <RightColumn
              ownedRelics={ownedRelics}
              trinketLimit={trinketLimit}
              brazierLitCount={brazierLitCount}
              brazierTotalCount={brazierTotalCount}
              enemySlainCount={enemySlainCount}
              enemyTotalCount={enemyTotalCount}
              onOpenRelicModal={(index) => setSelectedRelicIndex(index)}
            />
          </div>
        </div>

        <section
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: layout.deckLeft,
            right: layout.deckRight,
            minWidth: 0,
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          <DeckGrid
            deckCards={deckCards}
            minCardWidth={layout.minCardWidth}
            open={open}
          />
        </section>
      </div>

      <OwnedRelicModal
        open={selectedRelicIndex != null}
        relics={ownedRelics}
        selectedIndex={selectedRelicIndex}
        onClose={() => setSelectedRelicIndex(null)}
        onSelectIndex={setSelectedRelicIndex}
        viewport={viewport}
      />
    </div>
  );
}
